import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar, { Pager } from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { Orders } from '../api/admin';

const PAGE_SIZE = 50;

const OrdersTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(Number(filters.page) || 1);
    const [searchCert, setSearchCert] = useState(filters.certNumber || '');
    const [searchOrder, setSearchOrder] = useState(filters.orderNumber || '');
    const [searchBuyer, setSearchBuyer] = useState(filters.buyerEmail || '');
    const [searchSeller, setSearchSeller] = useState(filters.sellerEmail || '');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setSearchCert(filters.certNumber || '');
        setSearchOrder(filters.orderNumber || '');
        setSearchBuyer(filters.buyerEmail || '');
        setSearchSeller(filters.sellerEmail || '');
        setPage(Number(filters.page) || 1);
    }, [filters.certNumber, filters.orderNumber, filters.buyerEmail, filters.sellerEmail, filters.page]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Orders.list({
                page,
                count: PAGE_SIZE,
                certNumber: searchCert || undefined,
                orderNumber: searchOrder || undefined,
                buyerEmail: searchBuyer || undefined,
                sellerEmail: searchSeller || undefined,
            });
            setRows(result.orders || []);
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, searchCert, searchOrder, searchBuyer, searchSeller]);

    useEffect(() => { load(); }, [load]);

    const applySearch = () =>
        goTo({
            tab: 'orders',
            certNumber: searchCert || undefined,
            orderNumber: searchOrder || undefined,
            buyerEmail: searchBuyer || undefined,
            sellerEmail: searchSeller || undefined,
            page: 1,
        });
    const clearFilters = () => goTo({ tab: 'orders' });

    const columns = [
        { key: 'id', header: 'ID', width: '70px' },
        {
            key: 'order_number',
            header: 'Order #',
            render: (r) =>
                r.order_number ? (
                    <Link to={`/orders/${r.order_number}`} className="external-link" onClick={(e) => e.stopPropagation()}>
                        {r.order_number}
                    </Link>
                ) : '—',
        },
        {
            key: 'cert_number',
            header: 'Cert',
            render: (r) =>
                r.cert_number ? (
                    <RelLink tab="certs" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                        {r.cert_number}
                    </RelLink>
                ) : '—',
        },
        {
            key: 'listing_id',
            header: 'Listing',
            render: (r) =>
                r.listing_id ? (
                    <RelLink tab="listings" filters={{ listingId: r.listing_id }} goTo={goTo}>
                        {r.listing_id}
                    </RelLink>
                ) : '—',
        },
        {
            key: 'buyer_email',
            header: 'Buyer',
            render: (r) =>
                r.buyer_email ? (
                    <RelLink tab="users" filters={{ email: r.buyer_email }} goTo={goTo}>
                        {r.buyer_email}
                    </RelLink>
                ) : '—',
        },
        {
            key: 'seller_email',
            header: 'Seller',
            render: (r) =>
                r.seller_email ? (
                    <RelLink tab="users" filters={{ email: r.seller_email }} goTo={goTo}>
                        {r.seller_email}
                    </RelLink>
                ) : '—',
        },
        {
            key: 'price',
            header: 'Price',
            render: (r) => (r.price !== undefined ? `$${Number(r.price).toFixed(2)}` : '—'),
        },
    ];

    const sectionsFor = (r) => [
        {
            title: 'Order',
            rows: [
                { label: 'ID', value: r.id },
                {
                    label: 'Order #',
                    render: () =>
                        r.order_number ? (
                            <Link to={`/orders/${r.order_number}`} className="external-link">{r.order_number}</Link>
                        ) : '—',
                },
                { label: 'Price', value: r.price !== undefined ? `$${Number(r.price).toFixed(2)}` : '—' },
                { label: 'Confirmation #', value: r.order_confirmation_id },
            ],
        },
        {
            title: 'Related',
            rows: [
                {
                    label: 'Cert',
                    render: () =>
                        r.cert_number ? (
                            <RelLink tab="certs" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                                {r.cert_number}
                            </RelLink>
                        ) : '—',
                },
                {
                    label: 'Listing',
                    render: () =>
                        r.listing_id ? (
                            <RelLink tab="listings" filters={{ listingId: r.listing_id }} goTo={goTo}>
                                {r.listing_id}
                            </RelLink>
                        ) : '—',
                },
                {
                    label: 'Buyer',
                    render: () =>
                        r.buyer_email ? (
                            <RelLink tab="users" filters={{ email: r.buyer_email }} goTo={goTo}>
                                {r.buyer_email}
                            </RelLink>
                        ) : '—',
                },
                {
                    label: 'Seller',
                    render: () =>
                        r.seller_email ? (
                            <RelLink tab="users" filters={{ email: r.seller_email }} goTo={goTo}>
                                {r.seller_email}
                            </RelLink>
                        ) : '—',
                },
            ],
        },
    ];

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input placeholder="Order #" value={searchOrder} onChange={(e) => setSearchOrder(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()} />
                <input placeholder="Cert #" value={searchCert} onChange={(e) => setSearchCert(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()} />
                <input placeholder="Buyer email" value={searchBuyer} onChange={(e) => setSearchBuyer(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()} />
                <input placeholder="Seller email" value={searchSeller} onChange={(e) => setSearchSeller(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()} />
                <button className="btn-primary" onClick={applySearch}>Search</button>
                <button className="btn-ghost" onClick={clearFilters}>Clear</button>
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={rows}
                    onRowClick={(r) => setSelected(r)}
                    emptyMessage="No orders match these filters."
                />
            )}

            <Pager
                page={page}
                totalPages={rows.length === PAGE_SIZE ? page + 1 : page}
                onChange={(p) => goTo({
                    tab: 'orders',
                    certNumber: searchCert || undefined,
                    orderNumber: searchOrder || undefined,
                    buyerEmail: searchBuyer || undefined,
                    sellerEmail: searchSeller || undefined,
                    page: p,
                })}
            />

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Order ${selected.order_number || selected.id}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? sectionsFor(selected) : []}
            />
        </div>
    );
};

export default OrdersTab;
